import {
  AlertCircle,
  Archive,
  ArchiveRestore,
  Check,
  CheckCircle2,
  FolderKanban,
  FolderPlus,
  LoaderCircle,
  PencilLine,
  RefreshCw,
  Search,
  X,
} from "lucide-react";

import {
  useEffect,
  useMemo,
  useState,
  type FormEvent,
} from "react";

import {
  ApiRequestError,
  updateProject,
} from "./api";

import type {
  Project,
} from "./types";

type ProjectFilter =
  | "active"
  | "archived"
  | "all";

const PROJECT_COLORS = [
  "#5D50E7",
  "#2563EB",
  "#0891B2",
  "#059669",
  "#65A30D",
  "#D97706",
  "#DC2626",
  "#DB2777",
];

function getErrorMessage(
  error: unknown,
): string {
  if (error instanceof ApiRequestError) {
    return error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "Не удалось сохранить проект.";
}

function ProjectsPanel({
  projects,
  isLoading,
  error,
  onRefresh,
  onCreateProject,
  onChanged,
  onSessionExpired,
  onClose,
}: {
  projects: Project[];
  isLoading: boolean;
  error: string | null;

  onRefresh:
    () => Promise<void>;

  onCreateProject:
    () => void;

  onChanged:
    (project: Project) => Promise<void>;

  onSessionExpired:
    () => Promise<void>;

  onClose:
    () => void;
}) {
  const [searchQuery, setSearchQuery] =
    useState("");

  const [filter, setFilter] =
    useState<ProjectFilter>("active");

  const [
    selectedProject,
    setSelectedProject,
  ] = useState<Project | null>(null);

  const [name, setName] =
    useState("");

  const [description, setDescription] =
    useState("");

  const [color, setColor] =
    useState("#5D50E7");

  const [isSaving, setIsSaving] =
    useState(false);

  const [localError, setLocalError] =
    useState<string | null>(null);

  const [successMessage, setSuccessMessage] =
    useState<string | null>(null);

  useEffect(() => {
    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow =
      "hidden";

    function handleKeyDown(
      event: KeyboardEvent,
    ): void {
      if (event.key === "Escape") {
        onClose();
      }
    }

    window.addEventListener(
      "keydown",
      handleKeyDown,
    );

    return () => {
      document.body.style.overflow =
        previousOverflow;

      window.removeEventListener(
        "keydown",
        handleKeyDown,
      );
    };
  }, [onClose]);

  useEffect(() => {
    if (!selectedProject) {
      return;
    }

    const refreshedProject =
      projects.find(
        (project) =>
          project.id === selectedProject.id,
      );

    if (refreshedProject) {
      setSelectedProject(
        refreshedProject,
      );
    } else {
      setSelectedProject(null);
    }
  }, [projects]);

  useEffect(() => {
    if (!selectedProject) {
      setName("");
      setDescription("");
      setColor("#5D50E7");
      setLocalError(null);
      setSuccessMessage(null);
      return;
    }

    setName(selectedProject.name);

    setDescription(
      selectedProject.description ?? "",
    );

    setColor(
      selectedProject.color
        ?? "#5D50E7",
    );

    setLocalError(null);
    setSuccessMessage(null);
  }, [selectedProject]);

  const normalizedSearch =
    searchQuery
      .trim()
      .toLocaleLowerCase("ru-RU");

  const filteredProjects = useMemo(
    () => projects.filter((project) => {
      if (
        filter === "active"
        && project.isArchived
      ) {
        return false;
      }

      if (
        filter === "archived"
        && !project.isArchived
      ) {
        return false;
      }

      if (normalizedSearch.length > 0) {
        const searchableValue = [
          project.name,
          project.description ?? "",
        ]
          .join(" ")
          .toLocaleLowerCase("ru-RU");

        if (
          !searchableValue.includes(
            normalizedSearch,
          )
        ) {
          return false;
        }
      }

      return true;
    }),
    [
      filter,
      normalizedSearch,
      projects,
    ],
  );

  const activeCount = projects.filter(
    (project) => !project.isArchived,
  ).length;

  const archivedCount = projects.filter(
    (project) => project.isArchived,
  ).length;

  async function saveProject(
    isArchived: boolean,
  ): Promise<void> {
    if (
      !selectedProject
      || isSaving
      || name.trim().length === 0
    ) {
      return;
    }

    setIsSaving(true);
    setLocalError(null);
    setSuccessMessage(null);

    try {
      const response = await updateProject(
        selectedProject.id,
        {
          name: name.trim(),

          description:
            description.trim().length > 0
              ? description.trim()
              : null,

          color,
          isArchived,
        },
      );

      setSelectedProject(
        response.data,
      );

      await onChanged(response.data);

      setSuccessMessage(
        isArchived
          ? "Проект перемещён в архив."
          : selectedProject.isArchived
            ? "Проект восстановлен."
            : "Изменения сохранены.",
      );
    } catch (requestError: unknown) {
      if (
        requestError instanceof ApiRequestError
        && requestError.status === 401
      ) {
        await onSessionExpired();
        return;
      }

      setLocalError(
        getErrorMessage(requestError),
      );
    } finally {
      setIsSaving(false);
    }
  }

  async function handleSubmit(
    event: FormEvent<HTMLFormElement>,
  ): Promise<void> {
    event.preventDefault();

    await saveProject(
      selectedProject?.isArchived
        ?? false,
    );
  }

  return (
    <div className="projects-panel-backdrop">
      <section
        className="projects-panel"
        role="dialog"
        aria-modal="true"
        aria-label="Управление проектами"
      >
        <header className="projects-panel-header">
          <div className="projects-panel-icon">
            <FolderKanban size={24} />
          </div>

          <div className="projects-panel-heading">
            <span>Рабочее пространство</span>
            <h2>Проекты</h2>

            <p>
              Создавай направления,
              редактируй и архивируй их.
            </p>
          </div>

          <div className="projects-panel-header-actions">
            <button
              type="button"
              className="projects-refresh-button"
              onClick={() => {
                void onRefresh();
              }}
              disabled={isLoading}
            >
              <RefreshCw
                size={18}
                className={
                  isLoading
                    ? "rotating"
                    : ""
                }
              />

              <span>Обновить</span>
            </button>

            <button
              type="button"
              className="projects-create-button"
              onClick={onCreateProject}
            >
              <FolderPlus size={18} />
              <span>Новый проект</span>
            </button>

            <button
              type="button"
              className="projects-close-button"
              onClick={onClose}
              aria-label="Закрыть проекты"
            >
              <X size={21} />
            </button>
          </div>
        </header>

        <div className="projects-panel-summary">
          <article>
            <FolderKanban size={20} />

            <div>
              <span>Всего проектов</span>
              <strong>{projects.length}</strong>
            </div>
          </article>

          <article>
            <CheckCircle2 size={20} />

            <div>
              <span>Активные</span>
              <strong>{activeCount}</strong>
            </div>
          </article>

          <article>
            <Archive size={20} />

            <div>
              <span>В архиве</span>
              <strong>{archivedCount}</strong>
            </div>
          </article>
        </div>

        <div className="projects-panel-content">
          <aside className="projects-list-side">
            <label className="projects-search-field">
              <Search size={18} />

              <input
                type="search"
                value={searchQuery}
                onChange={(event) => {
                  setSearchQuery(
                    event.target.value,
                  );
                }}
                placeholder="Найти проект"
              />

              {searchQuery.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery("");
                  }}
                  aria-label="Очистить поиск"
                >
                  <X size={16} />
                </button>
              )}
            </label>

            <div className="projects-filter-buttons">
              <button
                type="button"
                className={
                  filter === "active"
                    ? "projects-filter-active"
                    : ""
                }
                onClick={() => {
                  setFilter("active");
                }}
              >
                Активные
              </button>

              <button
                type="button"
                className={
                  filter === "archived"
                    ? "projects-filter-active"
                    : ""
                }
                onClick={() => {
                  setFilter("archived");
                }}
              >
                Архив
              </button>

              <button
                type="button"
                className={
                  filter === "all"
                    ? "projects-filter-active"
                    : ""
                }
                onClick={() => {
                  setFilter("all");
                }}
              >
                Все
              </button>
            </div>

            {error && (
              <div className="projects-list-error">
                <AlertCircle size={17} />
                <span>{error}</span>
              </div>
            )}

            <div className="projects-list-heading">
              <span>Найдено</span>

              <strong>
                {filteredProjects.length}
              </strong>
            </div>

            <div className="projects-list">
              {isLoading
                && projects.length === 0 ? (
                <div className="projects-list-loading">
                  <LoaderCircle
                    size={24}
                    className="rotating"
                  />

                  <span>
                    Загружаем проекты…
                  </span>
                </div>
              ) : filteredProjects.length === 0 ? (
                <div className="projects-list-empty">
                  <FolderKanban size={29} />

                  <strong>
                    Проекты не найдены
                  </strong>

                  <span>
                    Измени поиск или создай
                    новый проект.
                  </span>
                </div>
              ) : (
                filteredProjects.map(
                  (project) => (
                    <button
                      key={project.id}
                      type="button"
                      className={[
                        "projects-list-card",

                        selectedProject?.id
                          === project.id
                          ? "projects-list-card-selected"
                          : "",

                        project.isArchived
                          ? "projects-list-card-archived"
                          : "",
                      ].join(" ")}
                      onClick={() => {
                        setSelectedProject(
                          project,
                        );
                      }}
                    >
                      <span
                        className="projects-list-color"
                        style={{
                          backgroundColor:
                            project.color
                            ?? "#CBD5E1",
                        }}
                      />

                      <span className="projects-list-copy">
                        <strong>
                          {project.name}
                        </strong>

                        <small>
                          {project.description
                            ?? "Без описания"}
                        </small>
                      </span>

                      {project.isArchived && (
                        <span className="projects-archived-badge">
                          Архив
                        </span>
                      )}
                    </button>
                  ),
                )
              )}
            </div>
          </aside>

          <main className="project-editor-side">
            {selectedProject ? (
              <form
                className="project-editor"
                onSubmit={(event) => {
                  void handleSubmit(event);
                }}
              >
                <div className="project-editor-heading">
                  <div className="project-editor-icon">
                    <PencilLine size={20} />
                  </div>

                  <div>
                    <span>
                      Проект #{selectedProject.id}
                    </span>

                    <h3>
                      Редактирование проекта
                    </h3>

                    <p>
                      Изменения сразу появятся
                      в задачах и боковом меню.
                    </p>
                  </div>
                </div>

                {localError && (
                  <div className="project-editor-message project-editor-error">
                    <AlertCircle size={18} />
                    <span>{localError}</span>
                  </div>
                )}

                {successMessage && (
                  <div className="project-editor-message project-editor-success">
                    <CheckCircle2 size={18} />
                    <span>{successMessage}</span>
                  </div>
                )}

                <label className="project-editor-field">
                  <span>
                    Название
                    <strong>*</strong>
                  </span>

                  <input
                    type="text"
                    value={name}
                    onChange={(event) => {
                      setName(
                        event.target.value,
                      );

                      setSuccessMessage(null);
                    }}
                    maxLength={160}
                    required
                    disabled={isSaving}
                  />
                </label>

                <label className="project-editor-field">
                  <span>Описание</span>

                  <textarea
                    value={description}
                    onChange={(event) => {
                      setDescription(
                        event.target.value,
                      );

                      setSuccessMessage(null);
                    }}
                    rows={6}
                    maxLength={2000}
                    placeholder="Цель и содержание проекта"
                    disabled={isSaving}
                  />
                </label>

                <fieldset className="project-editor-colors">
                  <legend>
                    Цвет проекта
                  </legend>

                  <div>
                    {PROJECT_COLORS.map(
                      (projectColor) => (
                        <button
                          key={projectColor}
                          type="button"
                          className={[
                            "project-editor-color",

                            color === projectColor
                              ? "project-editor-color-selected"
                              : "",
                          ].join(" ")}
                          style={{
                            backgroundColor:
                              projectColor,
                          }}
                          onClick={() => {
                            setColor(projectColor);
                            setSuccessMessage(null);
                          }}
                          aria-label={
                            `Выбрать цвет ${projectColor}`
                          }
                          disabled={isSaving}
                        >
                          {color === projectColor && (
                            <Check size={15} />
                          )}
                        </button>
                      ),
                    )}

                    <label className="project-custom-color">
                      <input
                        type="color"
                        value={color}
                        onChange={(event) => {
                          setColor(
                            event.target.value
                              .toUpperCase(),
                          );

                          setSuccessMessage(null);
                        }}
                        disabled={isSaving}
                      />

                      <span>Другой</span>
                    </label>
                  </div>
                </fieldset>

                <div className="project-preview">
                  <span
                    style={{
                      backgroundColor: color,
                    }}
                  />

                  <div>
                    <small>Предпросмотр</small>
                    <strong>
                      {name.trim()
                        || "Название проекта"}
                    </strong>
                  </div>
                </div>

                <div className="project-editor-actions">
                  <button
                    type="button"
                    className={[
                      "project-archive-button",

                      selectedProject.isArchived
                        ? "project-restore-button"
                        : "",
                    ].join(" ")}
                    onClick={() => {
                      void saveProject(
                        !selectedProject
                          .isArchived,
                      );
                    }}
                    disabled={
                      isSaving
                      || name.trim().length === 0
                    }
                  >
                    {selectedProject.isArchived ? (
                      <>
                        <ArchiveRestore size={17} />
                        Восстановить
                      </>
                    ) : (
                      <>
                        <Archive size={17} />
                        В архив
                      </>
                    )}
                  </button>

                  <button
                    type="submit"
                    className="project-save-button"
                    disabled={
                      isSaving
                      || name.trim().length === 0
                    }
                  >
                    {isSaving ? (
                      <>
                        <LoaderCircle
                          size={17}
                          className="rotating"
                        />

                        Сохраняем…
                      </>
                    ) : (
                      <>
                        <CheckCircle2 size={17} />
                        Сохранить
                      </>
                    )}
                  </button>
                </div>
              </form>
            ) : (
              <div className="project-editor-empty">
                <FolderKanban size={38} />

                <strong>
                  Выбери проект
                </strong>

                <span>
                  Здесь появятся его параметры,
                  цвет и управление архивом.
                </span>

                <button
                  type="button"
                  onClick={onCreateProject}
                >
                  <FolderPlus size={17} />
                  Создать проект
                </button>
              </div>
            )}
          </main>
        </div>
      </section>
    </div>
  );
}

export default ProjectsPanel;
